import { IAlertsByAlertType } from "app/models/statistics.model";
import { ExpandCollapseAnimation } from "app/animations";
import { HelperService, SortOrder } from "app/services/helper.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CommonModule, SlicePipe } from "@angular/common";
import { AlertTypePipe, DeviceTypePipe } from "app/pipes/pipes";
import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
} from "@angular/core";
import { StatisticsExportMode, StatisticsExportSnapshot, StatisticsExportWidget, StatisticsPdfTableRow } from "app/services/statistics-export.models";
import { renderNativeSnapshotToPng } from "app/services/statistics-native-canvas.util";
import { StatisticsPngDownloadBtnComponent } from "../statistics-png-download-btn/statistics-png-download-btn.component";

@Component({
  selector: "alerts-per-alert-type",
  templateUrl: "./alerts-per-alert-type.component.html",
  styleUrls: ["./alerts-per-alert-type.component.css"],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SlicePipe,
    DeviceTypePipe,
    AlertTypePipe,
    StatisticsPngDownloadBtnComponent,
  ],
  animations: [ExpandCollapseAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertsPerAlertTypeComponent implements OnInit, StatisticsExportWidget {
  constructor(
    private helper: HelperService,
    private translate: TranslateService
  ) {}

  @Input() alertTypeAlerts: IAlertsByAlertType[];
  listedAlerts: IAlertsByAlertType[];

  expandState: "collapsed" | "expanded" = "collapsed";
  collapseStateDeviceTypeCount: number = 3;

  displayedRows: number = 0;
  maxRows: number;
  sorter: string = "";
  sortOrder: SortOrder = SortOrder.Default;

  // Style object for the alerts container
  alertsContainerStyles: { "grid-template-columns": string };

  // Set width of the alert count columns based on number digits of the biggest alert count
  setAlertColumnWidths(maxAlerts: number): void {
    let alertCountGridColumnWidth = "auto";
    // 1-digit
    if (maxAlerts < 10) {
      alertCountGridColumnWidth = "12px";
    }
    // 2-digits
    else if (maxAlerts < 100) {
      alertCountGridColumnWidth = "20px";
    }
    // 3-digits
    else if (maxAlerts < 1000) {
      alertCountGridColumnWidth = "28px";
    }
    // 4-digits
    else if (maxAlerts < 10000) {
      alertCountGridColumnWidth = "36px";
    }
    // 5-digits
    else if (maxAlerts < 100000) {
      alertCountGridColumnWidth = "44px";
    }
    // 6-digits
    else if (maxAlerts < 1000000) {
      alertCountGridColumnWidth = "52px";
    }
    // Set styles
    this.alertsContainerStyles = {
      "grid-template-columns": `${alertCountGridColumnWidth} auto`,
    };
  }

  sort(sorter: "alertType" | "alerts"): void {
    if (this.sorter !== sorter) {
      this.sortOrder = SortOrder.Default;
    }
    this.sorter = sorter;

    switch (this.sortOrder) {
      case SortOrder.Default:
        if (sorter === "alertType") {
          this.listedAlerts = this.alertTypeAlerts.sort((a, b) =>
            this.compareAlertTypeNamesDesc(a, b, this.helper)
          );
        }
        if (sorter === "alerts") {
          this.listedAlerts = this.alertTypeAlerts.sort(
            this.compareAlertsCountsDesc
          );
        }
        this.sortOrder = SortOrder.Desc;
        break;
      case SortOrder.Desc:
        if (sorter === "alertType") {
          this.listedAlerts = this.alertTypeAlerts.sort((a, b) =>
            this.compareAlertTypeNamesAsc(a, b, this.helper)
          );
        }
        if (sorter === "alerts") {
          this.listedAlerts = this.alertTypeAlerts.sort(
            this.compareAlertsCountsAsc
          );
        }
        this.sortOrder = SortOrder.Asc;
        break;
      case SortOrder.Asc:
        this.listedAlerts = this.alertTypeAlerts;
        this.sortOrder = SortOrder.Default;
        break;
    }
  }

  compareAlertTypeNamesAsc(
    a: IAlertsByAlertType,
    b: IAlertsByAlertType,
    helper: HelperService
  ): number {
    const deviceTypeNameA = helper.getDeviceType(a.deviceType);
    const deviceTypeNameB = helper.getDeviceType(b.deviceType);

    return deviceTypeNameB.localeCompare(deviceTypeNameA);
  }

  compareAlertTypeNamesDesc(
    a: IAlertsByAlertType,
    b: IAlertsByAlertType,
    helper: HelperService
  ): number {
    const deviceTypeNameA = helper.getDeviceType(a.deviceType);
    const deviceTypeNameB = helper.getDeviceType(b.deviceType);

    return deviceTypeNameA.localeCompare(deviceTypeNameB);
  }

  compareAlertsCountsAsc(a: IAlertsByAlertType, b: IAlertsByAlertType): number {
    return a.totalAlerts - b.totalAlerts;
  }

  compareAlertsCountsDesc(
    a: IAlertsByAlertType,
    b: IAlertsByAlertType
  ): number {
    return b.totalAlerts - a.totalAlerts;
  }

  toggleExpand(): void {
    this.expandState =
      this.expandState === "collapsed" ? "expanded" : "collapsed";
  }

  ngOnInit(): void {
    // Sort first so the row counts below reflect the order actually rendered.
    // (Otherwise displayedRows counts the first N device types in the original
    // order while the list shows the first N after sorting by count — the
    // "Showing X / Y" total then mismatches the visible rows.)
    this.applyDefaultSort();

    let count = 0;

    // Count total rows
    for (let i = 0; i < this.alertTypeAlerts.length; i++) {
      this.alertTypeAlerts[i].activations.forEach(() => count++);
      count++;

      if (i < this.collapseStateDeviceTypeCount) {
        this.displayedRows = count;
      }
    }
    this.maxRows = count;

    // Get biggest alert count
    const maxAlertCount = Math.max(
      ...this.alertTypeAlerts.map((alert) => alert.totalAlerts)
    );
    this.setAlertColumnWidths(maxAlertCount);
  }

  /** Default UI order: device types by count desc (alpha tiebreaker), activations by count desc. */
  private applyDefaultSort(): void {
    this.sortActivationsByCountDesc();
    this.alertTypeAlerts.sort((a, b) => {
      const countDiff = b.totalAlerts - a.totalAlerts;
      if (countDiff !== 0) {
        return countDiff;
      }
      const nameA = this.helper.getDeviceType(a.deviceType);
      const nameB = this.helper.getDeviceType(b.deviceType);
      return nameA.localeCompare(nameB);
    });
    this.listedAlerts = this.alertTypeAlerts;
    this.sorter = "alerts";
    this.sortOrder = SortOrder.Desc;
  }

  private sortActivationsByCountDesc(): void {
    this.alertTypeAlerts.forEach((alert) => {
      alert.activations.sort((a, b) => b.alertCount - a.alertCount);
    });
  }

  async captureForExport(mode: StatisticsExportMode = 'pdf'): Promise<StatisticsExportSnapshot | null> {
    const snapshot = this.buildNativeTableSnapshot();
    // Both PDF and PNG render this grouped list natively (device-type headers +
    // indented activations) — no html2canvas. PDF embeds the structured table;
    // the per-widget PNG download rasterizes the same data straight to a canvas.
    if (mode === 'pdf') {
      return snapshot;
    }
    return renderNativeSnapshotToPng(snapshot, (key) => this.translate.instant(key));
  }

  private buildNativeTableSnapshot(): StatisticsExportSnapshot {
    // Mirror the on-screen default order (see applyDefaultSort): count desc,
    // then device-type name asc — so the exported PDF matches the UI on ties.
    const sortedTypes = [...(this.alertTypeAlerts ?? [])].sort((a, b) => {
      const countDiff = b.totalAlerts - a.totalAlerts;
      if (countDiff !== 0) {
        return countDiff;
      }
      return this.helper
        .getDeviceType(a.deviceType)
        .localeCompare(this.helper.getDeviceType(b.deviceType));
    });

    const rows: StatisticsPdfTableRow[] = [];
    for (const type of sortedTypes) {
      rows.push({
        cells: [this.helper.getDeviceType(type.deviceType), String(type.totalAlerts)],
        group: true,
      });
      const activations = [...type.activations].sort((a, b) => b.alertCount - a.alertCount);
      for (const activation of activations) {
        rows.push({
          cells: [
            this.helper.getActivationName(activation.group, activation.node, type.deviceType, '', 0),
            String(activation.alertCount),
          ],
          indent: true,
        });
      }
    }

    return {
      slug: 'alerts-per-alert-type',
      titleKey: 'ALERTS_PER_ALERT_TYPE',
      imageDataUrl: '',
      pdfNativeTable: {
        columns: [
          { headerKey: 'ALERT_TYPE', align: 'left', width: '*' },
          { headerKey: 'ALERTS_WITH_FIRST_LETTER_UPPERCASE', align: 'right', width: 'auto' },
        ],
        rows,
        columnGroups: 3,
      },
    };
  }
}
